const J = "/img/work/";

export const CASE_STUDIES = {
  junction: {
    slug: "junction",
    name: "Junction",
    index: "Case study 01",
    accent: "#B184FF",
    kicker: "Marketplace · Mobile · Trust & Safety",
    tagline: "A marketplace where verified students buy and sell used goods on campus, built around keeping every trade safe.",
    meta: [
      { k: "Platforms", v: "iOS, Android, admin portal" },
      { k: "Stack", v: "Flutter, Node.js, Firebase" },
      { k: "Team", v: "5 people" },
      { k: "Timeline", v: "5 months" },
    ],
    hero: { type: "phones", imgs: [J + "j-home.jpg", J + "j-jauction.jpg", J + "j-chat.jpg"] },
    stats: [
      { v: "2,500+", l: "Verified students" },
      { v: "4.2", l: "App Store & Google Play" },
      { v: "200+", l: "Listings" },
      { v: "20%", l: "Sell-through rate" },
    ],
    problem: {
      title: ["Students were already trading.", "It just wasn't safe."],
      body: [
        "Every semester, students buy and sell textbooks, furniture and electronics, mostly through WhatsApp groups and OLX, or they give things away. None of these channels can confirm the other person is a student.",
        "Before writing any code, we surveyed more than 400 students. Three problems came up again and again.",
      ],
      cardsLabel: "What 400+ students told us",
      cards: [
        { t: "Scams", b: "Fraud and fake listings on existing platforms were common." },
        { t: "Unsafe meetups", b: "Students didn't feel safe meeting strangers at unfamiliar locations." },
        { t: "Price and distance", b: "Good prices were hard to find, and sellers were often too far away, which made furniture and bulky items especially hard to buy." },
      ],
      aside: { t: "A sustainability initiative", b: "Keeping goods in use within campus communities reduces waste and the demand for new products. Junction makes reuse the easy option for students." },
    },
    research: {
      label: "Research",
      title: "No existing platform verified its users or was built for students.",
      intro: "Benchmarking against OLX, Facebook Marketplace, Quikr, Cashify and NoBroker. None offered a student-only marketplace, verified users, bidding or rentals. A SWOT for each competitor surfaced the same weaknesses: scams, unverified sellers and spam listings.",
      items: [
        { t: "The gap", b: "Trust. Every large marketplace treats verification as optional." },
        { t: "The opportunity", b: "A closed community of verified students, trading close to campus." },
        { t: "The risk to manage", b: "Listing supply and seasonal demand, addressed with referrals and campus crews." },
      ],
    },
    approach: {
      title: "Every product decision started with one question: how do we protect the student on the other side of the trade?",
      items: [
        { t: "Verified students only", b: "College email onboards instantly. Everyone else completes a human-reviewed check with a selfie, college ID and government ID, in 24 hours on average." },
        { t: "Human moderation", b: "One admin queue for reviewing verifications, moderating listings and resolving reports." },
        { t: "Trades stay local", b: "Listings are ranked by popularity and distance. Buyers and sellers agree on a meetup spot pinned on a map, close to campus." },
        { t: "Conversations stay in the app", b: "Real-time chat carries negotiation, offers and attachments, so students never share phone numbers." },
        { t: "Visible accountability", b: "Ratings, trade history and reviews on every profile. Anyone can be reported or blocked, and reported users can no longer contact the reporter." },
        { t: "Bidding is optional", b: "Many students prefer fixed prices, so Jauction, the auction feature, is a choice sellers make rather than the default." },
      ],
    },
    system: {
      title: "Purple for trust, orange for action.",
      body: "We designed for Rhea, 20, at Manipal University, moving into a flat with friends. Her dislikes became our rules: no ads, verification before entry, and a clean, predictable interface with no glitches.",
      swatches: [
        { n: "Charcoal Black", h: "#262626" },
        { n: "Lavender Mist", h: "#F6F6FF" },
        { n: "Deep Purple", h: "#3F2B81" },
        { n: "Pastel Violet", h: "#B184FF" },
        { n: "Punchy Orange", h: "#FF680C" },
      ],
      specs: [
        { t: "Typography", b: "Manrope, mobile scale 12–24 px" },
        { t: "Layout", b: "4-column grid, 20 px margins" },
        { t: "States", b: "Error, validation and success colours" },
        { t: "Icons", b: "Phosphor set on a 32 px grid" },
      ],
    },
    screensTitle: ["Getting in, finding,", "deciding and talking."],
    screens: [
      { device: "phone", img: J + "j-create.jpg", t: "Create account", notes: ["College email onboards instantly with a confirmation code.", "No college email? A human-reviewed ID check, not a dead end.", "Stays disabled until a valid email is entered."] },
      { device: "phone", img: J + "j-home.jpg", t: "Home", notes: ["Chat and notifications always in the top bar.", "Category tiles for one-tap browsing.", "Crew Clash turns referrals into a campus team contest.", "\u201cPick up where you left off\u201d brings back viewed items."] },
      { device: "phone", img: J + "j-search.jpg", t: "Search with filters", notes: ["\u201cNear Me\u201d comes first: distance was a top survey complaint.", "Type separates fixed-price listings from Jauction auctions.", "Condition chips give every listing comparable facts."] },
      { device: "phone", img: J + "j-swipe.jpg", t: "Swipeable results", notes: ["Swipe to the next result without going back.", "Seller and time posted shown before the product.", "Chat is the only action, so every negotiation starts in Junction."] },
      { device: "phone", img: J + "j-jauction.jpg", t: "Jauction Room", notes: ["Live current bid, bidders and bids placed.", "Countdown on the photo, impossible to miss.", "Named, timed bid history keeps it transparent."] },
      { device: "phone", img: J + "j-chats.jpg", t: "Chat list", notes: ["Initials, message previews and relative times.", "An orange unread dot uses the action colour."] },
      { device: "phone", img: J + "j-chat.jpg", t: "Buyer–seller chat", notes: ["A pinned product card ties the chat to one listing.", "Offer cards: the seller sends a price, the buyer confirms in one tap.", "Attachments for extra photos or bills."] },
      { device: "phone", img: J + "j-profile.jpg", t: "Profile", notes: ["College and city confirm a real student community.", "Rating and items sold as headline stats.", "Reviews from past buyers."] },
      { device: "phone", img: J + "j-report.jpg", t: "Report listing", notes: ["A flag on every listing.", "Preset reasons match survey problems and help moderators sort reports.", "Submit stays disabled until a reason is picked."] },
    ],
    hard: {
      title: "Auctions need a payment guarantee. Students don't want to deposit money in a new app.",
      lead: "We used UPI mandates instead of a wallet. The money never leaves the student's bank account until they have won, inspected and accepted the item.",
      flow: [
        { t: "1 · Authorise once", b: "Before a first bid, the student approves a one-time UPI mandate blocking 25% of the starting bid." },
        { t: "2 · Bid in Jauction", b: "Bids update live. The blocked amount stays in the student's own account." },
        { t: "3a · Win", b: "The student inspects the item and accepts it. Only then is the amount debited." },
        { t: "3b · Lose", b: "The mandate is cancelled. There is no refund to wait for, because nothing was taken." },
      ],
      items: [
        { t: "Handling Aadhaar data responsibly", b: "Identity documents are captured in the app's camera with explicit consent. We never store Aadhaar numbers. Images are encrypted with AES-256-GCM, seen only during human review, and permanently deleted once a decision is made." },
        { t: "Real-time chat at marketplace scale", b: "Chat runs on Firebase Firestore's real-time sync, which scales without socket servers to manage. Each conversation is tied to one listing, buyer and seller, with push notifications when the app is closed." },
        { t: "Authentication and moderation", b: "Login is passwordless, with a one-time code sent to the student's email. Moderators can open a reported conversation's full history, including attachments, to decide what action to take." },
      ],
    },
    results: {
      title: ["Live on iOS and Android,", "trusted in four cities."],
      stats: [
        { v: "2,500+", l: "Verified students across Hyderabad, Bangalore, Manipal and Mangalore" },
        { v: "4.2", l: "Rating on the App Store and Google Play" },
        { v: "200+", l: "Listings posted" },
        { v: "40", l: "Completed transactions, a 20% sell-through rate" },
        { v: "24 h", l: "Average manual verification time" },
      ],
    },
    next: "gamesense",
  },

  gamesense: {
    slug: "gamesense",
    name: "Gamesense",
    index: "Case study 02",
    accent: "#2EE59D",
    kicker: "Sports Tech · Edge AI · Computer Vision",
    tagline: "A replay tablet beside every court, so players can settle line calls and save highlights without internet or an app.",
    meta: [
      { k: "Product", v: "Court tablet, venue software, dashboard" },
      { k: "Stack", v: "React, Node.js, Python" },
      { k: "Team", v: "3 people, ongoing" },
      { k: "Status", v: "Pilot planned in Hyderabad" },
    ],
    hero: { type: "tablet", imgs: [J + "g-ingame.jpg"], caption: "The court tablet during a game: two camera angles, one tap to review a call or save a highlight." },
    stats: [
      { v: "<5s", l: "Replay start target" },
      { v: "120fps", l: "Global-shutter cameras" },
      { v: "2", l: "Camera angles per court" },
      { v: "₹0", l: "Upfront cost to venues" },
    ],
    problem: {
      title: ["In recreational racket sports,", "nobody makes the call."],
      body: [
        "In pickleball, tennis, badminton, squash and padel played for fun, there is no umpire. Line calls, serves and net touches are settled by argument. Players also have no easy way to capture a great rally, even though they share their games on WhatsApp and Instagram.",
        "Venues face a different problem. Court booking is a commodity, so they need a premium extra that sets them apart, but most sports centres don't want another monthly software bill.",
      ],
      cardsLabel: "Who it's for",
      cards: [
        { t: "Competitive club player", b: "Plays 3–4 times a week and values fairness above all. Uses replay mostly to settle disputes." },
        { t: "Social player", b: "Plays weekly for fun. Wants highlight clips and slow motion to share a great point." },
        { t: "Venue manager", b: "Runs 4–20 courts and cares about occupancy and retention. Needs reliable hardware, little maintenance and usage insight." },
      ],
    },
    research: {
      label: "Competitor landscape",
      title: "Court cameras are established abroad and just arriving in India. Most are built for highlights, not fairness.",
      table: [
        ["PlaySight", "Global", "Multi-angle video, AI analysis, live streaming and instant replay on thousands of courts"],
        ["Wingfield", "Europe", "AI cameras with automated highlights, coaching replays and livestreams"],
        ["PUSHIT", "Europe", "Venue subscription of €89–199 per court per month"],
        ["Save My Play", "US", "Players scan a QR code courtside to record, with no app or account"],
        ["Hudle Vision", "India", "Highlights and performance data on padel courts"],
        ["Clutch", "India", "Scan-a-QR recording with AI highlights and analytics in its own app"],
        ["FieldFlicks", "India", "Camera-as-a-service with 4K recordings and highlight clips"],
      ],
      note: "From public websites and press coverage, September 2026.",
      items: [
        { t: "Built for settling disputes", b: "A dispute replay one tap away on a tablet at the court, not analytics watched afterwards." },
        { t: "Works without internet", b: "Replay and slow motion run on a small computer at the venue." },
        { t: "No app for players", b: "Clips arrive by email or link." },
        { t: "No cost to the venue", b: "Players pay ₹100 per hour and the venue earns a 20% revenue share." },
      ],
    },
    approach: {
      title: "Fairness first, with no internet or app required.",
      flow: [
        { t: "2 cameras", b: "Global shutter, 120 fps, covering the whole court" },
        { t: "Venue computer", b: "Rolling buffer, replay, slow motion, clip encoding" },
        { t: "Court tablet", b: "Replay, tag, clip and send, served locally" },
        { t: "Venue dashboard", b: "Court status, usage, clips, hardware health" },
        { t: "Cloud", b: "Clip sharing with 7-day expiring links" },
        { t: "Player", b: "Branded clip by email, no app" },
      ],
      flowNote: "If the internet drops, replay and clip saving keep working. Sharing waits until the connection returns.",
      items: [
        { t: "One button, under 5 seconds", b: "A first-time player should start a replay within 5 seconds. Buttons are at least 80 px tall and usable one-handed, since players hold a racket." },
        { t: "Edge-first architecture", b: "A small computer at the venue keeps the last 30–60 seconds and the full session, starts replays within 2 seconds and encodes clips. The cloud only shares clips." },
        { t: "Colour-coded tagging", b: "Every replay is tagged Dispute, Highlight or Analysis, and the same colours carry into the venue dashboard." },
        { t: "Privacy by design", b: "Recordings are deleted 30 minutes after a session. Saved clips expire after 7 days. No facial recognition, and never raw footage." },
        { t: "A model that suits Indian venues", b: "Three months free, then players pay per session by UPI on the tablet. Venues invest nothing upfront." },
        { t: "Shared on the player's terms", b: "Branded clips go straight to the player's email. Raw footage never leaves the venue." },
      ],
    },
    screensTitle: ["A dispute settled and a", "highlight sent, in a minute."],
    screens: [
      { device: "tablet", img: J + "g-welcome.jpg", t: "1 · Walk up and tap", notes: ["No app and no login.", "Players enter a name, court and booking length."] },
      { device: "tablet", img: J + "g-dispute.jpg", t: "2 · Contest a call", notes: ["Replay starts instantly.", "Drop to 0.25x and ball tracking marks the bounce."] },
      { device: "tablet", img: J + "g-highlight.jpg", t: "3 · Clip a highlight", notes: ["Scrub the whole session.", "Set start and end points."] },
      { device: "tablet", img: J + "g-send.jpg", t: "4 · Preview and send", notes: ["A branded clip goes to the player's email.", "Raw footage never leaves the venue."] },
      { device: "browser", img: J + "g-dash.jpg", t: "Venue dashboard · Overview", notes: ["Weekly replays, clips saved and shared, and uptime against targets.", "Courts right now: live status, time left and hardware issues.", "Replay reasons per court, with a plain-language insight."] },
      { device: "browser", img: J + "g-clips.jpg", t: "Venue dashboard · Clips", notes: ["Filter by tag and court.", "Download, or keep a clip for 30 days."] },
    ],
    screensNote: "Camera feeds in the demo are simulated until the pilot starts. Dashboard shown as a design mockup with sample data.",
    hard: {
      title: "Seeing the ball clearly, even when a player is in the way.",
      img: J + "g-review.jpg",
      imgCaption: "Dispute review at 0.25x: rear and side cameras side by side, with the tracked ball and its trail.",
      items: [
        { t: "Detecting the ball on a busy court", b: "Courts are full of round objects that look like the ball in play: spare balls, lights, logos and shapes on clothing. We trained a YOLO object-detection model on more than 700 images, chosen to teach it the ball in play and ignore everything else." },
        { t: "Keeping the ball visible", b: "With one camera, the ball disappears whenever a player stands between it and the lens, usually at the moment that matters. A second camera covers the court from another angle, so at least one always sees the ball." },
        { t: "Global shutter at 120 fps", b: "A global shutter captures the whole frame at once, so smashes and serves stay sharp and undistorted, and 0.25x slow motion plays back smoothly." },
      ],
    },
    results: {
      title: ["In development, with a", "pilot planned in Hyderabad."],
      note: "The tablet prototype and venue dashboard design are complete. These are the targets the pilot will measure; real numbers will replace them once it runs.",
      stats: [
        { v: "< 5 s", l: "Replay start for 95% of taps" },
        { v: "99%", l: "Uptime during venue hours" },
        { v: "30%", l: "Of saved clips shared" },
        { v: "10", l: "Venues within 6 months" },
      ],
      roadmap: "Automatic highlight detection, player profiles, shot and rally counts, and coach annotation tools, all on the same cameras.",
    },
    next: "junction",
  },
};
